import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useHistory, Redirect } from 'react-router-dom';
import { ServiceStatusAction } from '../../actions';
import { Translate as t } from '../../config/Translate';
import { Loading } from '../../components/common/Loading';
import { ENV_DEVELOPMENT } from '../../config/appConstants';

const POLLING_INTERVAL = 30000; // 30초

const AdminServiceStatus = ({ serviceStatus, GetStatus }) => {
	const history = useHistory();
	const timerRef = useRef(null);
	const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
	const isSuperAdmin = userInfo.adminType === 'S';

	const wsConnected = serviceStatus.wsConnected;

	useEffect(() => {
		GetStatus(); // 초기 1회는 항상 호출
		if (!wsConnected) {
			timerRef.current = setInterval(() => {
				GetStatus();
			}, POLLING_INTERVAL);
		}
		return () => {
			if (timerRef.current) clearInterval(timerRef.current);
		};
	}, [wsConnected]);

	const formatElapsed = (minutes) => {
		const MINUTES_PER_HOUR = 60;
		const MINUTES_PER_DAY = 1440;
		const MINUTES_PER_MONTH = 43200; // ~30일
		const MINUTES_PER_YEAR = 525600; // ~365일

		if (minutes >= MINUTES_PER_YEAR) {
			const y = Math.floor(minutes / MINUTES_PER_YEAR);
			return `${y} ${t('service.status.unitYear')}`;
		}
		if (minutes >= MINUTES_PER_MONTH) {
			const mo = Math.floor(minutes / MINUTES_PER_MONTH);
			return `${mo} ${t('service.status.unitMonth')}`;
		}
		if (minutes >= MINUTES_PER_DAY) {
			const d = Math.floor(minutes / MINUTES_PER_DAY);
			return `${d} ${t('service.status.unitDay')}`;
		}
		if (minutes >= MINUTES_PER_HOUR) {
			const h = Math.floor(minutes / MINUTES_PER_HOUR);
			return `${h} ${t('service.status.unitHour')}`;
		}
		return `${minutes} ${t('service.status.unitMin')}`;
	};

	const handleItemClick = (ticketKey) => {
		history.push(`/Admin/AdminService/${ticketKey}`);
	};

	// Feature Flag Off 시 대시보드로 redirect (dev 환경은 허용)
	if (!serviceStatus.enabled && process.env.NODE_ENV !== ENV_DEVELOPMENT) {
		return <Redirect to="/Admin/Dashboard" />;
	}

	const renderLevel = (level, levelData, color, title, desc) => {
		return (
			<div className="col-md-4 mb-2">
				<div className="card" style={{ borderTop: `3px solid ${color}`, overflow: 'hidden' }}>
					<div className="card-header" style={{ backgroundColor: color + '15', padding: '12px 16px' }}>
						<div className="d-flex justify-content-between align-items-center">
							<div>
								<span className="badge me-2" style={{ backgroundColor: color, color: '#fff' }}>
									{level}
								</span>
								<strong>{t(title)}</strong>
							</div>
							<span className="badge bg-secondary">{levelData.count}</span>
						</div>
						<small className="text-muted mt-1 d-block">{t(desc)}</small>
					</div>
					<div className="card-body p-0" style={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'hidden' }}>
						{levelData.items.length === 0 ? (
							<div className="text-center text-muted py-4">
								{t('service.status.noItems')}
							</div>
						) : (
							<ul className="listview simple-listview no-space" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
								{levelData.items.map((item, idx) => (
									<li key={item._id} onClick={() => handleItemClick(item.ticketKey)}
										style={{
											cursor: 'pointer',
											padding: '6px 12px',
											borderBottom: idx < levelData.items.length - 1 ? '1px solid #e9ecef' : 'none',
										}}>
										<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', width: '100%' }}>
											<div style={{ minWidth: 0, overflow: 'hidden' }}>
												<span className="text-primary" style={{ fontSize: '12px' }}>{item.ticketKey}</span>
												<div style={{
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
													color: '#343a40',
												}}>
													{item.summary || item.title || ''}
												</div>
											</div>
											<span style={{
												fontSize: '11px',
												backgroundColor: '#e9ecef',
												color: '#495057',
												whiteSpace: 'nowrap',
												flexShrink: 0,
												padding: '3px 8px',
												borderRadius: '4px',
												minWidth: '65px',
												textAlign: 'center',
											}}>
												{formatElapsed(item.elapsedMinutes)}
											</span>
										</div>
									</li>
								))}
							</ul>
						)}
					</div>
				</div>
			</div>
		);
	};

	return (
		<>
			<div className="extraHeader">
				<h2 className="mb-0">{t('service.status.title')}</h2>
				{isSuperAdmin && (
					<div className="ray-right">
						<button
							className="btn btn-outline-primary btn-sm mt-0"
							onClick={() => history.push('/Admin/AdminNotificationEmailSetting')}
						>
							{t('notification.label.emailSetting')}
						</button>
					</div>
				)}
			</div>
			<div id="appCapsule" className="extra-header-active full-height">
				<div className="section mt-2">
					{serviceStatus.isLoading && !serviceStatus.lastUpdated && (
						<div className="text-center py-5">
							<Loading />
						</div>
					)}
					<div className="row mt-1">
						{renderLevel('Lv1', serviceStatus.level1, '#dc3545', 'SERVICE_STATUS_LEVEL1_TITLE', 'SERVICE_STATUS_LEVEL1_DESC')}
						{renderLevel('Lv2', serviceStatus.level2, '#fd7e14', 'SERVICE_STATUS_LEVEL2_TITLE', 'SERVICE_STATUS_LEVEL2_DESC')}
						{renderLevel('Lv3', serviceStatus.level3, '#ffc107', 'SERVICE_STATUS_LEVEL3_TITLE', 'SERVICE_STATUS_LEVEL3_DESC')}
					</div>
				</div>
			</div>
		</>
	);
};

const mapState = (state) => ({
	serviceStatus: state.ServiceStatusReducer,
});

const mapDispatch = (dispatch) => ({
	GetStatus: () => dispatch(ServiceStatusAction.GetStatus()),
});

export default connect(mapState, mapDispatch)(AdminServiceStatus);
